import { gameView } from '../../../packages/domain/src/50-game-engine.mjs';
import { rotatePipe, pipeNetwork } from '../../../packages/domain/src/57-score-puzzles.mjs';
import { gameCellArtwork } from './36-game-artwork.mjs';

function rotatedCell(previous, accepted, events) {
  if (previous.data.stage !== accepted.data.stage) return -1;
  const before = previous.data.board,
    after = accepted.data.board;
  if (before.length !== 9 || after.length !== 9) return -1;
  const changed = before.flatMap((mask, index) => (mask === after[index] ? [] : [index]));
  const intent = events.find((event) => event.type === 'rotate')?.cells?.[0];
  const index = Number.isInteger(intent) ? intent : changed[0];
  if (!Number.isInteger(index) || index < 0 || index > 8 || changed.some((cell) => cell !== index))
    return -1;
  return rotatePipe(before[index]) === after[index] ? index : -1;
}

/** One actual clockwise turn, followed by accepted connected-channel lighting.
 * Native buttons, labels and board state never become an old snapshot; original
 * material ghosts own presentation only. Connectivity/order comes from57.
 * Unsupported graphics and cancellation reveal the accepted native scene;
 * generation guards prevent old animations settling a newer round. */
export function createPipePlayback(ctx, board, { settle, onClear, visible = () => true } = {}) {
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const animations = new Set(),
    ghosts = new Set();
  let generation = 0,
    disposed = false,
    current = null;
  function release() {
    for (const animation of animations) animation.cancel();
    for (const ghost of ghosts) ghost.remove();
    animations.clear();
    ghosts.clear();
    delete board.dataset.pipePlayback;
  }
  function cancel(reveal = false) {
    const interrupted = current;
    generation++;
    current = null;
    release();
    if (reveal && interrupted && !disposed && board.isConnected)
      interrupted.settle?.(interrupted.state);
  }
  function eligible() {
    return !disposed && board.isConnected && !document.hidden && !media.matches && visible();
  }
  function lifecycle() {
    if (!eligible()) cancel(true);
  }
  function cellAt(index) {
    return [...board.children].find((cell) => cell.dataset.cell === String(index));
  }
  function animate(node, frames, duration, delay = 0) {
    if (typeof node?.animate !== 'function') return;
    const animation = node.animate(frames, {
      duration,
      delay,
      fill: 'forwards',
      easing: 'cubic-bezier(.2,.8,.2,1)',
    });
    animations.add(animation);
  }
  async function wait() {
    const batch = [...animations];
    await Promise.all(
      batch.map((animation) => animation.finished?.catch(() => {}) ?? Promise.resolve()),
    );
    for (const animation of batch) {
      animation.cancel();
      animations.delete(animation);
    }
  }
  function ghost(previous, cell, index) {
    const target = cellAt(index),
      rect = target?.getBoundingClientRect(),
      origin = board.getBoundingClientRect();
    if (!rect || rect.width < 1 || rect.height < 1) return null;
    const node = ctx.e('span', 'game-pipe-flight');
    node.setAttribute('aria-hidden', 'true');
    node.dataset.pipeCell = String(index);
    Object.assign(node.style, {
      left: `${rect.left - origin.left}px`,
      top: `${rect.top - origin.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
    });
    node.append(gameCellArtwork(ctx, previous, cell, index, '', ''));
    ghosts.add(node);
    board.append(node);
    return node;
  }
  function play(previous, accepted, eventsOrEarned, override) {
    cancel();
    if (
      accepted.gameId !== 'rotation-loop' ||
      previous.gameId !== accepted.gameId ||
      !eligible() ||
      typeof cellAt(0)?.animate !== 'function'
    )
      return null;
    const events = Array.isArray(eventsOrEarned) ? eventsOrEarned : gameView(accepted).events || [];
    const index = rotatedCell(previous, accepted, events);
    if (index < 0) return null;
    const token = generation,
      finish = override || settle;
    current = { state: accepted, settle: finish };
    const valid = () => token === generation && eligible();
    return (async () => {
      try {
        const cells = gameView(previous).cells;
        const pieces = cells.map((cell, position) => ghost(previous, cell, position));
        if (pieces.some((piece) => !piece)) return;
        board.dataset.pipePlayback = 'true';
        pieces[index].style.zIndex = '4';
        animate(
          pieces[index],
          [{ transform: 'rotate(0deg)' }, { transform: 'rotate(90deg)' }],
          260,
        );
        await wait();
        if (!valid()) return;
        for (const piece of ghosts) piece.remove();
        ghosts.clear();
        delete board.dataset.pipePlayback;
        const oldLit = new Set(pipeNetwork(previous.data).lit);
        const connected = pipeNetwork(accepted.data).lit.filter((cell) => !oldLit.has(cell));
        for (const [order, cell] of connected.entries())
          animate(
            cellAt(cell)?.firstChild,
            [
              { filter: 'brightness(.75) drop-shadow(0 0 0 var(--game-colour-0))', opacity: 0.65 },
              {
                filter: 'brightness(1.3) drop-shadow(0 0 8px var(--game-colour-0))',
                opacity: 1,
                offset: 0.55,
              },
              { filter: 'brightness(1) drop-shadow(0 0 2px var(--game-colour-0))', opacity: 1 },
            ],
            320,
            Math.min(240, order * 35),
          );
        if (connected.length)
          onClear?.({
            type: 'connected',
            cells: [...connected],
            points: accepted.score - previous.score,
            label: `${connected.length} ${connected.length === 1 ? 'channel' : 'channels'} connected`,
          });
        await wait();
      } catch {
        // Optional motion failure leaves the accepted connected scene usable.
      } finally {
        if (token === generation) {
          current = null;
          release();
          if (!disposed && board.isConnected) finish?.(accepted);
        }
      }
    })();
  }
  document.addEventListener('visibilitychange', lifecycle);
  media.addEventListener?.('change', lifecycle);
  return {
    play,
    cancel,
    dispose() {
      if (disposed) return;
      disposed = true;
      cancel();
      document.removeEventListener('visibilitychange', lifecycle);
      media.removeEventListener?.('change', lifecycle);
    },
  };
}
